import type { ErpArticle, ErpMappingRow, ErpMatchReason, ErpNameCheck, ErpPendingMatch, ErpSaveState } from '../../../api';
import { formatDecimal } from '../labels';
import type { StoreCopy } from '../storeCopy';

/**
 * The words of the product matching screen (CO-155, CO-170). Statuses
 * ("Matched", "Code matches") come from labels.ts statusFor; this file has the
 * sentences around them. Nothing here names a store platform: that comes from
 * storeCopy.
 *
 * There is no "sure" anywhere on this screen: an equal code is evidence, not
 * proof, and the screen says what the evidence is and whether the names agree.
 */

/** Why an article is suggested, as the fact a person can check. */
export const matchReasonText: Record<ErpMatchReason, string> = {
  same_code: 'The product’s SKU is this article’s code.',
  same_code_loose: 'The SKU and the code are the same digits, but only once leading zeros and separators are ignored.',
  barcode_is_sku: 'The product’s SKU is this article’s barcode.',
  sku_in_name: 'The product’s SKU appears in the article’s name.',
  similar_name: 'The names share words.',
};

/** What the name check says, as a sentence. */
export const nameCheckText: Record<ErpNameCheck, string> = {
  agree: 'The two names describe the same thing.',
  partial: 'The two names share only some words.',
  differ: 'The two names share almost nothing. Check this is the same product before you use it.',
  unknown: 'One of the names has no words to compare.',
};

/** The caution that goes with every suggestion: the badges say what it is based on. */
export const matchEvidenceLegend = 'An equal code is not proof: compare the names before you use an article.';

/** The most telling reason first: a barcode, a code, a code without zeros, a SKU in the name, a similar name. */
const reasonOrder: ErpMatchReason[] = ['barcode_is_sku', 'same_code', 'same_code_loose', 'sku_in_name', 'similar_name'];

export function primaryReason(reasons: ErpMatchReason[] | undefined): ErpMatchReason | undefined {
  if (!reasons || reasons.length === 0) return undefined;
  return [...reasons].sort((a, b) => reasonOrder.indexOf(a) - reasonOrder.indexOf(b))[0];
}

/** The states a person narrows the list to, most useful first. */
export const matchFilterOrder = ['no_code', 'unmatched', 'conflict', 'matched'] as const;

/** An article's stock for the website, without the words around it: "4 BUC", or why there is none. */
export function articleStockValue(article: ErpArticle): string {
  if (article.missing) return 'No longer reported by the ERP';
  if (!article.in_warehouses || article.quantity === undefined) return 'Not in a warehouse this website sells from';
  // A number and its unit never wrap apart.
  const unit = article.unit ? `\u00a0${article.unit}` : '';
  return `${formatDecimal(article.quantity)}${unit}`;
}

/** One line about an article's stock for the website, in words. */
export function articleStockText(article: ErpArticle): string {
  const value = articleStockValue(article);
  return article.missing || !article.in_warehouses || article.quantity === undefined ? value : `${value} in stock`;
}

/**
 * The sale price in two parts: the amount with its currency, and what a
 * person needs to read it, whether it includes VAT.
 */
export function articlePriceParts(article: ErpArticle, currency: string, includesTax: boolean | null): { amount: string; tax: string } {
  if (article.price === undefined) return { amount: 'No sale price in the ERP', tax: '' };
  // A number and its currency never wrap apart.
  const amount = `${formatDecimal(article.price, 2)}${currency ? ` ${currency}` : ''}`;
  if (includesTax === true) return { amount, tax: 'with VAT' };
  if (includesTax === false) return { amount, tax: 'without VAT' };
  return { amount, tax: 'VAT not confirmed in the store' };
}

/**
 * Why the store did not save a match, in words a person can act on. The
 * codes are the store's own (value_in_use, ...) plus Commerce Ops' (no_answer,
 * not_sent) and the checks made before anything is sent.
 */
export const matchRefusalText: Record<string, string> = {
  value_in_use: 'Another product in the store already has this code.',
  unknown_product: 'The store has no product with this SKU.',
  not_in_website: 'The product is not in this website.',
  unsupported_type: 'This kind of product does not take stock from the ERP.',
  integration_off: 'The ERP integration is turned off for this website in the store.',
  no_match_field: 'The website has not chosen which field holds the ERP code.',
  matches_by_sku: 'This website matches products by their SKU, so there is no separate code to save.',
  match_field_not_writable: 'This store matches by its GTIN field, a real barcode that is never overwritten with an ERP code.',
  field_not_writable: 'This store matches by its GTIN field, a real barcode that is never overwritten with an ERP code.',
  invalid_item: 'The store could not read the request.',
  store_error: 'The store could not save it. Try again in a moment.',
  refused: 'The store refused it.',
  no_answer: 'The store did not answer. It may be offline, or its scheduled tasks may not be running.',
  not_sent: 'The request could not be sent to the store.',
  // Checked before anything is sent.
  article_in_use: 'Another product already has this article’s code.',
  unknown_article: 'The ERP has no article with this code.',
  not_a_safe_suggestion: 'This pair is not offered for a batch. Review the product on its own.',
  replace_not_chosen: 'This product already has a match and replacing it was not chosen.',
  changed_since: 'The product’s code was changed after this match, so it was left as it is.',
};

export function refusalText(reason: string | undefined): string {
  return matchRefusalText[reason ?? ''] ?? 'The store refused it.';
}

/** What a request to save a match says, for a product's list row. */
export function pendingRowText(pending: ErpPendingMatch): string {
  const code = pending.requested_code;
  switch (pending.state) {
    case 'pending':
      return code ? `Saving ${code} in the store…` : 'Removing the match in the store…';
    case 'expired':
      return 'The store has not confirmed it yet';
    default:
      return refusalText(pending.reason);
  }
}

/** The sentence under a product in the queue: where it stands, in words. */
export function rowStatusText(row: ErpMappingRow): string {
  if (row.pending && row.pending.state !== 'confirmed') return pendingRowText(row.pending);
  switch (row.state) {
    case 'matched': {
      const article = row.article ? row.article.name : `Article ${row.article_code ?? row.erp_code}`;
      return row.conflicts_with && row.conflicts_with.length > 0 ? `${article} · Same code as ${row.conflicts_with.join(', ')}` : article;
    }
    case 'unmatched':
      return `The code ${row.erp_code} is not in the ERP`;
    case 'conflict':
      return 'Also in a website that takes stock from the ERP';
    default:
      return row.suggestion ? row.suggestion.article_name : 'No suggestion yet';
  }
}

// ---------------------------------------------------------------------------
// Saving: capability
// ---------------------------------------------------------------------------

/**
 * Why a website cannot save matches from here, and what to do about it. Said
 * before a person reviews, searches or uploads anything, so no time is spent
 * on work that cannot be saved. Returns null where saving works.
 */
export function cannotSaveText(save: ErpSaveState, words: StoreCopy): { title: string; body: string; update: boolean } | null {
  if (save.available) return null;
  switch (save.reason) {
    case 'extension_cannot': {
      const version = save.required_version ?? (words.platform === 'woocommerce' ? '0.5.0' : '1.8.0');
      const product = words.platform === 'woocommerce' ? 'the Commerce Ops plugin' : 'the Commerce Ops extension';
      const installed = save.installed_version ? ` This store runs ${save.installed_version}.` : '';
      return {
        title: 'This store cannot save matches from Commerce Ops yet',
        body: `Saving a match needs ${product} ${version} or newer.${installed}`,
        update: true,
      };
    }
    case 'field_not_writable':
      return {
        title: 'This store matches by its GTIN field',
        body: `A GTIN is a real barcode that other systems read, so Commerce Ops never writes an ERP code into it. To save matches from here, choose the plugin’s “ERP article code” field as the matching field (${words.settingsPath}).`,
        update: false,
      };
    case 'matches_by_sku':
      return {
        title: 'This website matches products by their SKU',
        body: `A product takes the article whose code is its SKU, so there is no separate code to save. To change a match, change the SKU in ${words.admin}.`,
        update: false,
      };
    case 'match_field_not_set':
      return {
        title: 'No field holds the ERP code yet',
        body: `Choose which ${words.matchField} holds the ERP article code in ${words.admin}: ${words.settingsPath}.`,
        update: false,
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Files and batches (CO-162, CO-170)
// ---------------------------------------------------------------------------

/** Not problems, but things a person should know about a row. */
export const matchNoteText: Record<string, string> = {
  already_asked: 'The store was already asked for this and has not answered.',
  zero_variants_sku: 'Another product has this SKU with different leading zeros. Check this is the one you meant.',
  zero_variants_code: 'Another article has this code with different leading zeros. Check this is the one you meant.',
  number_sku: 'This cell is a number in the file, so leading zeros were not available. Check the product.',
  number_code: 'This cell is a number in the file, so leading zeros were not available. Check the article.',
};

export function noteText(notes: string[] | undefined): string[] {
  return (notes ?? []).map((note) => matchNoteText[note]).filter((text): text is string => Boolean(text));
}

/** A short name for why a row of an imported file was left out. */
export const rowProblemTitle: Record<string, string> = {
  missing_sku: 'No store SKU',
  missing_code: 'No ERP code',
  code_not_text: 'Not plain text',
  unknown_product: 'Unknown product',
  ambiguous_product: 'Several products fit',
  unknown_article: 'Unknown article',
  ambiguous_article: 'Several articles fit',
  article_missing: 'Article no longer in the ERP',
  article_in_use: 'Article used by another product',
  duplicate_sku: 'Product listed twice',
  duplicate_article: 'Article asked for twice',
  store_cannot_save: 'Store cannot save matches',
  match_field_not_set: 'No matching field',
  matches_by_sku: 'Matches by SKU',
  field_not_writable: 'Field is never written',
  licence_inactive: 'Licence does not allow updates',
  not_a_safe_suggestion: 'Not offered for a batch',
};

export function problemTitle(reason: string | undefined): string {
  return rowProblemTitle[reason ?? ''] ?? 'Cannot be saved';
}

/** The accepted file types, for the picker and the drop zone. */
export const matchFileAccept = '.csv,.xlsx,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The server reads at most this much; a bigger file is turned back before it is sent. */
export const MAX_MATCH_FILE_BYTES = 4 * 1024 * 1024;

/**
 * The delimiter a spreadsheet in this person's language most likely wants:
 * Excel writes and reads semicolons where the decimal separator is a comma.
 */
export function defaultDelimiter(): 'comma' | 'semicolon' {
  try {
    return new Intl.NumberFormat().format(1.1).includes(',') ? 'semicolon' : 'comma';
  } catch {
    return 'comma';
  }
}

/** A file's bytes as base64, in pieces so a large file does not overflow the call stack. */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const piece = 0x8000;
  for (let at = 0; at < bytes.length; at += piece) {
    binary += String.fromCharCode(...bytes.subarray(at, at + piece));
  }
  return btoa(binary);
}

/** Hands a generated file to the browser's download. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The count of a thing, with its noun: "1 product", "3 products". */
export function countOf(n: number, singular: string, plural = `${singular}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? singular : plural}`;
}
